import {logger, UIUtil, Util} from 'yougile-util'

interface KeyboardEventLike {
  keyCode: number
  preventDefault(): void
}

const documentMouseDownHandlers: Array<(e: MouseEvent) => void> = []
let checkerEl: HTMLElement

document.addEventListener('mousedown', (event: MouseEvent) => {
  documentMouseDownHandlers.forEach((h: (e: MouseEvent) => void) => {
    if (h) {
      h(event)
    }
  })
})

export class ClickOut {
  public static onClickOut(
    element: HTMLElement,
    handler: (e: MouseEvent) => void
  ): number {
    const len = documentMouseDownHandlers.push((event: MouseEvent) => {
      if (
        event.target !== element &&
        !UIUtil.isDescendant(element, event.target as Node)
      ) {
        handler(event)
      }
    })
    return len - 1
  }

  public static offClickOut(id: number): void {
    documentMouseDownHandlers[id] = undefined
  }
}

export class UtilUi {
  public static canvasToBlob(
    canvas: HTMLCanvasElement,
    mimeType: string,
    quality = 1,
    cb?: (blob: Blob) => void,
    raw = true
  ): Promise<Blob> {
    return new Promise((ok, fail) => {
      const callback = (blob: Blob): void => {
        cb?.(blob)
        if (blob) {
          ok(blob)
        } else {
          fail('Failed to get blob from canvas')
        }
      }
      if ('toBlob' in canvas && raw) {
        canvas.toBlob(callback)
      } else {
        const dataUrl = canvas.toDataURL(mimeType, quality)
        const blobBin = atob(dataUrl.split(',')[1])
        const array: number[] = []
        for (let i = 0; i < blobBin.length; i++) {
          array.push(blobBin.charCodeAt(i))
        }
        callback(new Blob([new Uint8Array(array).buffer], {type: mimeType}))
      }
    })
  }

  static calcTextBounds(
    text: string,
    style?: Partial<CSSStyleDeclaration>
  ): {width: number; height: number} {
    if (!checkerEl) {
      checkerEl = document.createElement('div')
      checkerEl.style.opacity = '0'
      checkerEl.style.pointerEvents = 'none'
      checkerEl.style.position = 'fixed'
      checkerEl.style.zIndex = '-1'
      document.body.appendChild(checkerEl)
    }
    if (style) {
      for (const p of Object.keys(style)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        checkerEl.style[p] = style[p]
      }
    }
    checkerEl.textContent = text
    const {width, height} = checkerEl.getBoundingClientRect()
    return {width, height}
  }

  public static getCursorPosition(parent: Node): number {
    try {
      const selection = document.getSelection()
      const range = new Range()
      if (!selection.anchorNode || !parent.contains(selection.anchorNode)) {
        return undefined
      }
      range.setStart(parent, 0)
      range.setEnd(selection.anchorNode, selection.anchorOffset)
      return range.toString().length
    } catch (e) {
      logger.log('IE')
      return 0
    }
  }

  public static setCursorPosition(parent: Node, position: number): void {
    try {
      let child = parent.firstChild
      while (position > 0) {
        if (!child) {
          return
        }
        const length = child.textContent.length
        if (position > length) {
          position -= length
          const prev = child
          child = child.nextSibling
          if (!child && prev.nodeType === 3) {
            document.getSelection().collapse(prev, length)
          }
        } else {
          if (child.nodeType === 3 /* text node */) {
            document.getSelection().collapse(child, position)
            position -= length
            child = child.nextSibling
          } else {
            child = child.firstChild
          }
        }
      }
    } catch (e) {
      logger.log('IE')
    }
  }

  public static selectAllText(parent: Node): void {
    try {
      const selection = window.getSelection()
      const range = document.createRange()
      range.selectNodeContents(parent)
      selection.removeAllRanges()
      selection.addRange(range)
    } catch (e) {
      logger.log('IE')
    }
  }

  public static insertTextAtCursor(
    input: HTMLInputElement | HTMLTextAreaElement,
    text: string
  ): void {
    const start = input.selectionStart
    const end = input.selectionEnd
    const value = input.value
    input.value = value.slice(0, start) + text + value.slice(end)
    input.setSelectionRange(start + 1, start + 1)
  }

  public static testImage(file: File): void {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const img = new Image()
      img.src = reader.result as string
      img.style.position = 'fixed'
      img.style.top = '0'
      img.style.zIndex = '1000000'
      document.body.appendChild(img)
    }
  }

  public static handleKey(
    onEnter: () => void,
    onEsc = Util.noop
  ): (e: KeyboardEventLike) => void {
    return (e: KeyboardEventLike) => {
      if (e.keyCode === 13) {
        onEnter()
        e.preventDefault()
      } else if (e.keyCode === 27) {
        onEsc()
        e.preventDefault()
      }
    }
  }

  /**
   * Cross-browser el.remove()
   * Because of IE stupidity.
   */
  public static removeEl(el: Element): void {
    if (!el) {
      return
    }
    if ('remove' in (el as any)) {
      el.remove()
    } else {
      // IE
      const parent = el.parentNode
      parent.removeChild(el)
    }
  }
}

export const setDisplayName = (Component: any, name: string) => {
  if (Component) {
    Component.displayName = name
  }
}
